import React, { useState } from 'react';
import {
  RotateCcw,
  SkipBack,
  Play,
  Pause,
  SkipForward,
  RotateCw,
  ChevronUp,
  ChevronDown,
  X,
} from 'react-feather';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import CircularProgress from '@mui/material/CircularProgress';
import { AudioPlayerTrackProgress } from '@trinity/chat-components/components/AudioPlayer/Progress.Track';

interface AudioControlsProps {
  isPlaying: boolean;
  isLoading?: boolean;
  onTogglePlay: () => void;
  onPreviousTrack: () => void;
  onNextTrack: () => void;
  onSkipBackward: () => void;
  onSkipAhead: () => void;
  onStop: () => void;
  currentTime: number;
  duration: number;
  onScan: (time: number) => void;
  onSeek: (time: number) => void;
  disabledPreviousTrack?: boolean;
  disabledNextTrack?: boolean;
}

const ControlsContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 2,
});

const Button = styled('button')(({ theme }) => ({
  background: 'none',
  border: 'none',
  width: 26,
  height: 26,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  '&:hover': {
    backgroundColor: theme.isTrinityThemed ? theme.palette.gray.light5 : theme.palette.btnPrimary.bg,
  },
  '&:disabled': {
    svg: {
      fill: theme.palette.gray.light1,
      stroke: theme.palette.gray.light1,
    },
  },
  '&:disabled:hover': {
    cursor: 'default',
    background: 'none',
  },
  transition: 'background-color 110ms ease-in-out',
}));

const RotateButton = styled('button')(({ theme }) => ({
  background: 'none',
  border: 'none',
  width: 26,
  height: 26,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  '&:hover': {
    backgroundColor: theme.isTrinityThemed ? theme.palette.gray.light5 : theme.palette.btnPrimary.bg,
  },
  '&:disabled': {
    svg: {
      stroke: theme.palette.gray.light1,
    },
  },
  '&:disabled:hover': {
    background: 'none',
    cursor: 'default',
    svg: {
      stroke: theme.palette.gray.light1,
    },
  },
  transition: 'background-color 110ms ease-in-out',
}));

const Filled = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  svg: {
    fill: theme.palette.gray.dark1,
    stroke: theme.palette.gray.dark1,
  },
}));

const TimeDisplay = styled('span')(({ theme }) => ({
  fontSize: '0.8rem',
  minWidth: '50px',
  whiteSpace: 'nowrap',
  color: theme.palette.gray.dark1,
}));

const ProgressAndTimer = styled('div')({
  display: 'flex',
  marginTop: '4px',
  alignItems: 'center',
  width: '100%',
});

const ProgressAndTimerCompressed = styled('div')({
  display: 'flex',
  flex: 1,
  justifyContent: 'center',
  marginLeft: '8px',
});

const StyledProgressTrack = styled(AudioPlayerTrackProgress)({
  flex: 1,
  marginRight: '12px',
  position: 'relative',
});

const StyledChevronUp = styled(ChevronUp)(({ theme }) => ({
  color: theme.palette.gray.dark1,
  cursor: 'pointer',
  transition: `transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms`,
}));

const StyledChevronDown = styled(ChevronDown)(({ theme }) => ({
  color: theme.palette.gray.dark1,
  cursor: 'pointer',
  transition: `transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms`,
}));

function formatTime(seconds?: number) {
  if (!seconds || isNaN(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s < 10 ? '0' + s : s}`;
}

// TODO: implement disabled state (styling) for buttons

const AudioControls: React.FC<AudioControlsProps> = ({
  isPlaying,
  isLoading,
  onTogglePlay,
  onPreviousTrack,
  onNextTrack,
  onSkipBackward,
  onSkipAhead,
  onStop,
  currentTime,
  duration,
  onScan,
  onSeek,
  disabledPreviousTrack,
  disabledNextTrack,
}) => {
  const theme = useTheme();

  const [isExpanded, setIsExpanded] = useState(true);

  const switchExpandedState = () => {
    setIsExpanded((prev) => !prev);
  };

  const expandCollapseLabel = isExpanded ? 'Collapse audio controls' : 'Expand audio controls';

  if (isLoading) {
    return (
      <LoadingContainer>
        <CircularProgress size={18} thickness={5} sx={{ color: theme.palette.gray.dark1 }} />
        <LoadingLabel>Loading audio...</LoadingLabel>
        <Button type="button" aria-label="Stop playback" title="Stop playback" onClick={onStop}>
          <Filled>
            <X size={14} />
          </Filled>
        </Button>
      </LoadingContainer>
    );
  }

  return (
    <OuterContainer>
      {isExpanded ? (
        <>
          <HeaderRow>
            <TopRightActions>
              <Button
                type="button"
                style={{ width: 20, height: 20 }}
                aria-label={expandCollapseLabel}
                title={expandCollapseLabel}
                onClick={switchExpandedState}
              >
                <StyledChevronUp size={14} />
              </Button>
              <Button
                type="button"
                style={{ width: 20, height: 20 }}
                aria-label="Stop playback"
                title="Stop playback"
                onClick={onStop}
              >
                <Filled>
                  <X size={12} />
                </Filled>
              </Button>
            </TopRightActions>
          </HeaderRow>
          <ControlsContainer>
            <RotateButton
              type="button"
              aria-label="Skip back 15 seconds"
              title="Skip back 15 seconds"
              onClick={onSkipBackward}
              disabled={currentTime === 0}
            >
              <RotateCcw size={14} color={theme.palette.gray.dark1} />
            </RotateButton>

            <Button
              type="button"
              aria-label="Previous track"
              title="Previous track"
              onClick={onPreviousTrack}
              disabled={disabledPreviousTrack}
            >
              <Filled>
                <SkipBack size={14} />
              </Filled>
            </Button>

            <PlayPauseButton isPlaying={isPlaying} onTogglePlay={onTogglePlay} />

            <Button
              type="button"
              aria-label="Next track"
              title="Next track"
              onClick={onNextTrack}
              disabled={disabledNextTrack}
            >
              <Filled>
                <SkipForward size={14} />
              </Filled>
            </Button>

            <RotateButton
              type="button"
              aria-label="Skip ahead 15 seconds"
              title="Skip ahead 15 seconds"
              onClick={onSkipAhead}
              disabled={currentTime === duration}
            >
              <RotateCw size={14} color={theme.palette.gray.dark1} />
            </RotateButton>
          </ControlsContainer>
          <ProgressAndTimer>
            <StyledProgressTrack position={currentTime} scan={onScan} seek={onSeek} />
            <TimeDisplay>
              {formatTime(currentTime)} / {formatTime(duration)}
            </TimeDisplay>
          </ProgressAndTimer>
        </>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <ProgressAndTimerCompressed>
            <StyledProgressTrack position={currentTime} scan={onScan} seek={onSeek} />
          </ProgressAndTimerCompressed>
          <ControlsContainer>
            <PlayPauseButton isPlaying={isPlaying} onTogglePlay={onTogglePlay} />
            <Button
              type="button"
              aria-label={expandCollapseLabel}
              title={expandCollapseLabel}
              onClick={switchExpandedState}
            >
              <StyledChevronDown size={14} />
            </Button>
          </ControlsContainer>
        </div>
      )}
    </OuterContainer>
  );
};

const OuterContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 220,
});

interface PlayPauseButtonProps {
  isPlaying: boolean;
  onTogglePlay: () => void;
}

const PlayPauseButton: React.FC<PlayPauseButtonProps> = ({ isPlaying, onTogglePlay }) => (
  <Button
    type="button"
    aria-label={isPlaying ? 'Pause playback' : 'Play audio'}
    title={isPlaying ? 'Pause' : 'Play'}
    style={{ width: 34, height: 34 }}
    onClick={onTogglePlay}
  >
    <Filled>{isPlaying ? <Pause size={16} /> : <Play size={16} />}</Filled>
  </Button>
);

const HeaderRow = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  marginBottom: -4,
});

const TopRightActions = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 2,
});

const LoadingContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  padding: '4px 0',
});

const LoadingLabel = styled('span')(({ theme }) => ({
  fontSize: '0.8rem',
  color: theme.palette.gray.dark1,
  whiteSpace: 'nowrap',
}));

export { AudioControls };
export default AudioControls;
